import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchPlanTemplates, type PlanTemplateDto } from "./planTemplates";
import { planConfig } from "./platformUi";
import type { TenantPlan } from "./types";
import { getUserFacingMessage } from "./errors";

export const PLAN_ORDER: TenantPlan[] = ["trial", "basic", "standard", "premium"];

export type MergedPlanDisplay = {
  label: string;
  color: string;
  bgColor: string;
  price: number;
  maxMembers: number;
  maxAdmins: number;
  storage: number;
  features: string[];
};

export function mergePlanTemplateDto(plan: TenantPlan, row: PlanTemplateDto | undefined): MergedPlanDisplay {
  const cfg = planConfig[plan];
  if (!row) {
    return {
      label: cfg.label,
      color: cfg.color,
      bgColor: cfg.bgColor,
      price: cfg.price,
      maxMembers: cfg.maxMembers,
      maxAdmins: cfg.maxAdmins,
      storage: cfg.storage,
      features: [...cfg.features],
    };
  }
  return {
    label: row.label?.trim() || cfg.label,
    color: cfg.color,
    bgColor: cfg.bgColor,
    price: Number(row.priceAnnual),
    maxMembers: row.maxMembers,
    maxAdmins: row.maxAdmins,
    storage: row.storageMb,
    features: row.features?.length ? [...row.features] : [...cfg.features],
  };
}

export function usePlanTemplatesMerged(): {
  loading: boolean;
  error: string | null;
  mergedByPlan: Record<TenantPlan, MergedPlanDisplay>;
  reload: () => void;
} {
  const [rows, setRows] = useState<PlanTemplateDto[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPlanTemplates()
      .then((r) => {
        if (!cancelled) {
          setRows(r);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setRows(null);
          setError(getUserFacingMessage(e, "方案模板加载失败，请刷新。"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const mergedByPlan = useMemo(() => {
    const out: Partial<Record<TenantPlan, MergedPlanDisplay>> = {};
    for (const k of PLAN_ORDER) {
      const row = rows?.find((r) => r.plan === k);
      out[k] = mergePlanTemplateDto(k, row);
    }
    return out as Record<TenantPlan, MergedPlanDisplay>;
  }, [rows]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { loading, error, mergedByPlan, reload };
}
